import {
  FileText,
  FolderKanban,
  IdCard,
  Layers,
  LayoutDashboard,
} from "lucide-react";
import { useProjectGroup } from "@/hooks/useProjectGroups";
import { useProjects } from "@/hooks/useProjects";
import { useMeetingNote } from "@/hooks/useMeetingNotes";
import { Badge } from "@/components/ui/badge";
import { matchContextPath } from "../match";
import type { ContextParams, NavContextDef, NavItem } from "../types";

export const projectGroupPath = (id: string) => `/projects/groups/${id}`;

const COLOR_CLASSES: Record<string, string> = {
  blue: "bg-blue-500",
  green: "bg-green-500",
  yellow: "bg-yellow-500",
  red: "bg-red-500",
  purple: "bg-purple-500",
  orange: "bg-orange-500",
  pink: "bg-pink-500",
};

function useProjectGroupItems({ id, noteId }: ContextParams): NavItem[] {
  const base = projectGroupPath(id);
  // L6: project count on the sidebar item.
  const { data: projects } = useProjects();
  const projectCount = projects
    ? projects.filter((p) => !p.deleted_at && p.project_group_id === id).length
    : null;
  // The open meeting note is a child page under Meeting notes (D12).
  const { data: note } = useMeetingNote(noteId);
  const notesHref = `${base}/meeting-notes`;
  return [
    {
      key: "overview",
      label: "Overview",
      href: base,
      exact: true,
      icon: LayoutDashboard,
    },
    { key: "details", label: "Details", href: `${base}/details`, icon: IdCard },
    {
      key: "projects",
      label: "Projects",
      href: `${base}/projects`,
      icon: FolderKanban,
      badge: projectCount,
    },
    {
      key: "meeting-notes",
      label: "Meeting notes",
      href: notesHref,
      icon: FileText,
      children: noteId
        ? [
            {
              key: `meeting-notes/${noteId}`,
              label: note ? note.title || "Untitled" : "Meeting note",
              href: `${notesHref}/${noteId}`,
            },
          ]
        : undefined,
    },
  ];
}

function useProjectGroupHeader({ id }: ContextParams) {
  const { data: group } = useProjectGroup(id);
  if (!group) return {};
  return {
    kicker: group.number ? `Project group ${group.number}` : "Project group",
    title: group.name,
    status: (
      <span className="flex items-center gap-2">
        {group.color && (
          <span
            className={`inline-block h-3 w-3 rounded-full ${COLOR_CLASSES[group.color] ?? "bg-gray-500"}`}
          />
        )}
        <Badge variant={group.status === "active" ? "default" : "secondary"}>
          {group.status}
        </Badge>
      </span>
    ),
  };
}

export const projectGroupContext: NavContextDef = {
  type: "project-group",
  typeLabel: "Project group",
  icon: Layers,
  match: (pathname) =>
    matchContextPath("/projects/groups/:id/meeting-notes/:noteId", pathname, {
      id: ["new"],
    }) ?? matchContextPath("/projects/groups/:id", pathname, { id: ["new"] }),
  key: ({ id }) => `project-group:${id}`,
  base: ({ id }) => projectGroupPath(id),
  parent: () => ({ label: "Project groups", href: "/projects/groups" }),
  useHeader: useProjectGroupHeader,
  useItems: useProjectGroupItems,
};
